<template>
    <div class="container admin-table-page">
        <!-- Fila del título y flecha hacia atrás -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- Fila de filtros debajo del título a la izquierda -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="report-select"
                        v-model="fechaCierre"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="item in lstFechaCierre" :key="item.id" :value="item.fecha_cierre">
                            {{ item.fecha_cierre }}
                        </option>
                    </b-form-select>                  
                    <b-button
                        class="consult-btn ml-2"
                        @click="procesarCierre()"
                        :disabled="!fechaCierre"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <div v-if="!isLoading">
                <CorporateDataTable
                    :items="listCierreMensual"
                    :fields="allFields"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="showSearchTable"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="!showToolbarExtras"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-excel-btn"
                                @click="generaExcelCierreMensual()"
                                :disabled="!showToolbarExtras || listCierreMensual.length === 0"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                            </b-button>
                        </div>
                    </template>

                    <!-- Formateo de columnas específicas -->
                    <template #cell(nro_contrato)="data">
                        <div class="text-end">{{ data.item?.nro_contrato }}</div>
                    </template>
                    <template #cell(tasa)="data">
                        <div class="text-end">{{ formatDecimal(data.item?.tasa) }}</div>
                    </template>
                    <template #cell(valor_canon)="data">
                        <div class="text-end">{{ formatNumber(data.item?.valor_canon) }}</div>
                    </template>
                    <template #cell(total_valor_canon)="data">
                        <div class="text-end">{{ formatNumber(data.item?.total_valor_canon) }}</div>
                    </template>
                    <template #cell(pasivo_inicial)="data">
                        <div class="text-end">{{ formatNumber(data.item?.pasivo_inicial) }}</div>
                    </template>
                    <template #cell(total_cambio_pasivo)="data">
                        <div class="text-end">{{ formatNumber(data.item?.total_cambio_pasivo) }}</div>
                    </template>
                    <template #cell(total_pago_capital)="data">
                        <div class="text-end">{{ formatNumber(data.item?.total_pago_capital) }}</div>
                    </template>
                    <template #cell(total_intereses)="data">
                        <div class="text-end">{{ formatNumber(data.item?.total_intereses) }}</div>
                    </template>
                    <template #cell(pasivo_final)="data">
                        <div class="text-end">{{ formatNumber(data.item?.pasivo_final) }}</div>
                    </template>
                    <template #cell(activo_derecho_uso_bruto)="data">
                        <div class="text-end">{{ formatNumber(data.item?.activo_derecho_uso_bruto) }}</div>
                    </template>
                    <template #cell(total_gasto_amortizacion)="data">
                        <div class="text-end">{{ formatNumber(data.item?.total_gasto_amortizacion) }}</div>
                    </template>
                    <template #cell(tipo_tasa)="data">
                        <div class="text-start">{{ nombreTipoTasa(data.item?.tipo_tasa) }}</div>
                    </template>
                    <template #cell(puntos_adicionales)="data">
                        <div class="text-end">{{ formatDecimal(data.item?.puntos_adicionales) }}</div>
                    </template>
                    <template #cell(frecuencia)="data">
                        <div class="text-end">{{ formatNumber(data.item?.frecuencia) }}</div>
                    </template>
                    <template #cell(modalidad)="data">
                        <div class="text-start">{{ nombreModalidad(data.item?.modalidad) }}</div>
                    </template>
                    <template #cell(tasa_cambio_canon)="data">
                        <div class="text-end">{{ formatNumber(data.item?.tasa_cambio_canon) }}</div>
                    </template>
                    <template #cell(puntos_adicionales_cambio_canon)="data">
                        <div class="text-end">{{ formatDecimal(data.item?.puntos_adicionales_cambio_canon) }}</div>
                    </template>
                    <template #cell(periodicidad_cambio_canon)="data">
                        <div class="text-end">{{ formatNumber(data.item?.periodicidad_cambio_canon) }}</div>
                    </template>
                    <template #cell(estado)="data">
                        <div class="text-start">{{ data.item?.estado === 'Inicial' ? 'Vigente' : data.item?.estado }}</div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import * as XLSX from "exceljs";
import ContratoCierre from '../../services/ContratoCierre';
import TipoTasas from "../../services/TipoTasas";
import Authentication from "../../services/Authentication";
import CorporateDataTable from '../utils/CorporateDataTable.vue';

export default {
    name: 'CierreMensual',
    components: { CorporateDataTable },
    data: () => ({
        fechaCierre: '',
        lstFechaCierre: [],
        listCierreMensual: [],
        dataCierreMensual: [],
        btnGenExcel: false,
        lstTipoTasa: [],
        isLoading: false,
        showToolbarExtras: false
    }),
    computed: {
        labels() {
            if (this.$store.state._language === "es-ES") {
                return {
                    title: 'Consulta cierre mensual',
                    nroContrato: 'Contrato',
                    fechaCierre: 'Fecha cierre',
                    tasa: 'Tasa',
                    valorCanon: 'Valor canon',
                    totalValorCanon: 'Total valor canon',
                    pasivoInicial: 'Pasivo inicial',
                    totalCambioPasivo: 'Total cambio pasivo',
                    totalPagoCapital: 'Total pago capital',
                    totalIntereses: 'Total intereses',
                    pasivoFinal: 'Pasivo final',
                    activoDerechoUsoBruto: 'Activo derecho uso bruto',
                    totalGastoAmortizacion: 'Total gasto amortización',
                    tipoTasa: 'Tipo tasa',
                    puntosAdicionales: 'Puntos adicionales',
                    frecuencia: 'Frecuencia',
                    modalidad: 'Modalidad',
                    tasaCambioCanon: 'Tasa cambio canon',
                    puntosAdicionalesCambioCanon: 'Puntos cambio canon',
                    periodicidadCambioCanon: 'Periodicidad cambio canon',
                    estado: 'Estado',
                    consultar: 'Consultar',
                    selectReport: 'Seleccione Fecha',
                    loading: 'Cargando...',
                    noData: 'No hay datos disponibles',
                    noMatches: 'No se encontraron resultados',
                    searchPlaceholder: 'Buscar en la tabla...',
                    rowsPerPage: 'Filas por página:',
                    noSort: 'Sin orden',
                    sortAsc: 'A-Z / Menor a mayor',
                    sortDesc: 'Z-A / Mayor a menor',
                    columns: 'Columnas',
                    showAllColumns: 'Mostrar todas',
                    filters: 'Filtros',
                    clearFilters: 'Limpiar filtros',
                    filterNroContrato: 'Filtrar contrato...',
                    filterFechaCierre: 'Filtrar fecha cierre...',
                    filterTasa: 'Filtrar tasa...',
                    filterValorCanon: 'Filtrar valor canon...',
                    filterTotalValorCanon: 'Filtrar total valor canon...',
                    filterPasivoInicial: 'Filtrar pasivo inicial...',
                    filterTotalCambioPasivo: 'Filtrar total cambio pasivo...',
                    filterTotalPagoCapital: 'Filtrar total pago capital...',
                    filterTotalIntereses: 'Filtrar total intereses...',
                    filterPasivoFinal: 'Filtrar pasivo final...',
                    filterActivoDerechoUsoBruto: 'Filtrar activo derecho uso bruto...',
                    filterTotalGastoAmortizacion: 'Filtrar total gasto amortización...',
                    filterTipoTasa: 'Filtrar tipo tasa...',
                    filterPuntosAdicionales: 'Filtrar puntos adicionales...',
                    filterFrecuencia: 'Filtrar frecuencia...',
                    filterModalidad: 'Filtrar modalidad...',
                    filterTasaCambioCanon: 'Filtrar tasa cambio canon...',
                    filterPuntosAdicionalesCambioCanon: 'Filtrar puntos adicionales cambio canon...',
                    filterPeriodicidadCambioCanon: 'Filtrar periodicidad cambio canon...',
                    filterEstado: 'Filtrar estado...'
                }
            } else {
                return {
                    title: 'Monthly closing consultation',
                    nroContrato: 'Contrato',
                    fechaCierre: 'Closing date',
                    tasa: 'Rate',
                    valorCanon: 'Fee amount',
                    totalValorCanon: 'Total fee value',
                    pasivoInicial: 'Opening liabilities',
                    totalCambioPasivo: 'Complete passive shift',
                    totalPagoCapital: 'Total capital payment',
                    totalIntereses: 'Total interest',
                    pasivoFinal: 'Final liabilities',
                    activoDerechoUsoBruto: 'Gross right-of-use asset',
                    totalGastoAmortizacion: 'Total amortization expense',
                    tipoTasa: 'Rate type',
                    puntosAdicionales: 'Additional points',
                    frecuencia: 'Frequency',
                    modalidad: 'Mode',
                    tasaCambioCanon: 'Fee exchange rate',
                    puntosAdicionalesCambioCanon: 'Fee change points',
                    periodicidadCambioCanon: 'Frequency of fee changes',
                    estado: 'Statement',
                    consultar: 'Consult',
                    selectReport: 'Select Date',
                    loading: 'Loading...',
                    noData: 'No data available',
                    noMatches: 'No results found',
                    searchPlaceholder: 'Search in table...',
                    rowsPerPage: 'Rows per page:',
                    noSort: 'No sorting',
                    sortAsc: 'A-Z / Low to high',
                    sortDesc: 'Z-A / High to low',
                    columns: 'Columns',
                    showAllColumns: 'Show all',
                    filters: 'Filters',
                    clearFilters: 'Clear filters',
                    filterNroContrato: 'Filter by contract...',
                    filterFechaCierre: 'Filter by closing date...',
                    filterTasa: 'Filter by rate...',
                    filterValorCanon: 'Filter by canon value...',
                    filterTotalValorCanon: 'Filter by total canon value...',
                    filterPasivoInicial: 'Filter by initial liabilities...',
                    filterTotalCambioPasivo: 'Filter by total passive change...',
                    filterTotalPagoCapital: 'Filter by total capital payment...',
                    filterTotalIntereses: 'Filter by total interests...',
                    filterPasivoFinal: 'Filter by final passive...',
                    filterActivoDerechoUsoBruto: 'Filter by gross active right of use...',
                    filterTotalGastoAmortizacion: 'Filter by total amortization expense...',
                    filterTipoTasa: 'Filter by rate type...',
                    filterPuntosAdicionales: 'Filter by additional points...',
                    filterFrecuencia: 'Filter by frequency...',
                    filterModalidad: 'Filter by modality...',
                    filterTasaCambioCanon: 'Filter by canon exchange rate...',
                    filterPuntosAdicionalesCambioCanon: 'Filter by additional pts change canon...',
                    filterPeriodicidadCambioCanon: 'Filter by fee change frequency...',
                    filterEstado: 'Filter by state...'
                }
            }
        },
        allFields() {
            return [
                { key: 'nro_contrato', label: this.labels.nroContrato, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '100px' } },
                { key: 'fecha_cierre', label: this.labels.fechaCierre, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '120px' } },
                { key: 'tasa', label: this.labels.tasa, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '100px' } },
                { key: 'valor_canon', label: this.labels.valorCanon, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '120px' } },
                { key: 'total_valor_canon', label: this.labels.totalValorCanon, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'pasivo_inicial', label: this.labels.pasivoInicial, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'total_cambio_pasivo', label: this.labels.totalCambioPasivo, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'total_pago_capital', label: this.labels.totalPagoCapital, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'total_intereses', label: this.labels.totalIntereses, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'pasivo_final', label: this.labels.pasivoFinal, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'activo_derecho_uso_bruto', label: this.labels.activoDerechoUsoBruto, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'total_gasto_amortizacion', label: this.labels.totalGastoAmortizacion, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'tipo_tasa', label: this.labels.tipoTasa, sortable: true, thClass: 'text-center', tdClass: 'text-start align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '150px' } },
                { key: 'puntos_adicionales', label: this.labels.puntosAdicionales, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'frecuencia', label: this.labels.frecuencia, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '110px' } },
                { key: 'modalidad', label: this.labels.modalidad, sortable: true, thClass: 'text-center', tdClass: 'text-start align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '110px' } },
                { key: 'tasa_cambio_canon', label: this.labels.tasaCambioCanon, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'puntos_adicionales_cambio_canon', label: this.labels.puntosAdicionalesCambioCanon, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '140px' } },
                { key: 'periodicidad_cambio_canon', label: this.labels.periodicidadCambioCanon, sortable: true, thClass: 'text-center', tdClass: 'text-end align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '130px' } },
                { key: 'estado', label: this.labels.estado, sortable: true, thClass: 'text-center', tdClass: 'text-start align-middle', thStyle: { whiteSpace: 'nowrap', minWidth: '100px' } }
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'nro_contrato', type: 'text', placeholder: this.labels.filterNroContrato },
                { key: 'fecha_cierre', type: 'text', placeholder: this.labels.filterFechaCierre },
                { key: 'tasa', type: 'text', placeholder: this.labels.filterTasa },
                { key: 'valor_canon', type: 'text', placeholder: this.labels.filterValorCanon },
                { key: 'total_valor_canon', type: 'text', placeholder: this.labels.filterTotalValorCanon },
                { key: 'pasivo_inicial', type: 'text', placeholder: this.labels.filterPasivoInicial },
                { key: 'total_cambio_pasivo', type: 'text', placeholder: this.labels.filterTotalCambioPasivo },
                { key: 'total_pago_capital', type: 'text', placeholder: this.labels.filterTotalPagoCapital },
                { key: 'total_intereses', type: 'text', placeholder: this.labels.filterTotalIntereses },
                { key: 'pasivo_final', type: 'text', placeholder: this.labels.filterPasivoFinal },
                { key: 'activo_derecho_uso_bruto', type: 'text', placeholder: this.labels.filterActivoDerechoUsoBruto },
                { key: 'total_gasto_amortizacion', type: 'text', placeholder: this.labels.filterTotalGastoAmortizacion },
                { key: 'tipo_tasa', type: 'text', placeholder: this.labels.filterTipoTasa },
                { key: 'puntos_adicionales', type: 'text', placeholder: this.labels.filterPuntosAdicionales },
                { key: 'frecuencia', type: 'text', placeholder: this.labels.filterFrecuencia },
                { key: 'modalidad', type: 'text', placeholder: this.labels.filterModalidad },
                { key: 'tasa_cambio_canon', type: 'text', placeholder: this.labels.filterTasaCambioCanon },
                { key: 'puntos_adicionales_cambio_canon', type: 'text', placeholder: this.labels.filterPuntosAdicionalesCambioCanon },
                { key: 'periodicidad_cambio_canon', type: 'text', placeholder: this.labels.filterPeriodicidadCambioCanon },
                { key: 'estado', type: 'text', placeholder: this.labels.filterEstado }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        showSearchTable() {
            return true
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        async procesarCierre() {
            if (!this.fechaCierre) {
                this.$toast.error('Diligenciar la fecha.');
                return;
            }

            this.isLoading = true;

            try {
                const res = await ContratoCierre.getContratoCierreByFecha(this.fechaCierre);
                this.listCierreMensual = res.data.listContratosCerrados || [];
                this.dataCierreMensual = this.listCierreMensual;
                this.showToolbarExtras = this.listCierreMensual.length > 0;

                if (!this.listCierreMensual.length) {
                    this.$toast.error("No hay datos para la fecha seleccionada.");
                }
            } catch (err) {
                console.error(err);
                this.$toast.error("Error al consultar los datos.");
            } finally {
                this.isLoading = false;
            }
        },
        async generaExcelCierreMensual() {
            const { Workbook } = XLSX;
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet("Cierre mensual " + this.fechaCierre);

            // Título
            const titulo = ["Cierre mensual " + this.fechaCierre];
            worksheet.addRow(titulo);
            worksheet.mergeCells('A1:T1');
            worksheet.getCell('A1').font = { name: 'Arial', bold: true, size: 12 };
            worksheet.getCell('A1').alignment = { horizontal: 'center' };

            // Fila de información
            worksheet.addRow(['']);
            worksheet.mergeCells('A2:T2');

            const cell2 = worksheet.getCell('A2');
            cell2.value = {
                richText: [
                    { text: 'Generado por: ', font: { bold: true } },
                    { text: `${this.$store.state.userName} | ` },
                    { text: 'Fecha generación: ', font: { bold: true } },
                    { text: `${this.fechaActual()} | ` },
                    { text: 'Compañía: ', font: { bold: true } },
                    { text: `${this.nameCompania}` },
                ]
            };
            cell2.font = { name: 'Arial', size: 12 };
            cell2.alignment = { horizontal: 'center', vertical: 'middle' };

            worksheet.addRow(['']);
            worksheet.mergeCells('A3:T3');

            // Cabecera
            const headerRow = worksheet.addRow([
                'Nro. contrato',
                'Fecha cierre',
                'Tasas',
                'Valor canon',
                'Total valor canon',
                'Pasivo inicial',
                'Total cambio pasivo',
                'Total pago capital',
                'Total intereses',
                'Pasivo final',
                'Activo derecho de uso bruto',
                'Total gasto amortización',
                'Tipo tasa',
                'Puntos adicionales',
                'Frecuencia',
                'Modalidad',
                'Tasa cambio canon',
                'Puntos adicionales cambio canon',
                'Periodicidad cambio canon',
                'Estado',
            ]);
            headerRow.font = { name: 'Arial', bold: true, size: 12 };
            headerRow.alignment = { horizontal: 'center' };

            // Datos
            this.listCierreMensual.forEach((e) => {
                worksheet.addRow([
                    e.nro_contrato,
                    e.fecha_cierre,
                    this.formatDecimal(e.tasa),
                    this.formatNumber(e.valor_canon),
                    this.formatNumber(e.total_valor_canon),
                    this.formatNumber(e.pasivo_inicial),
                    this.formatNumber(e.total_cambio_pasivo),
                    this.formatNumber(e.total_pago_capital),
                    this.formatNumber(e.total_intereses),
                    this.formatNumber(e.pasivo_final),
                    this.formatNumber(e.activo_derecho_uso_bruto),
                    this.formatNumber(e.total_gasto_amortizacion),
                    this.nombreTipoTasa(e.tipo_tasa),
                    this.formatDecimal(e.puntos_adicionales),
                    this.formatNumber(e.frecuencia),
                    e.modalidad === 'V' ? 'Vencida' : 'Anticipada',
                    isNaN(this.formatNumber(e.tasa_cambio_canon)) ? 0 : this.formatNumber(e.tasa_cambio_canon),
                    isNaN(this.formatNumber(e.puntos_adicionales_cambio_canon)) ? 0 : this.formatDecimal(e.puntos_adicionales_cambio_canon),
                    isNaN(this.formatNumber(e.periodicidad_cambio_canon)) ? 0 : this.formatNumber(e.periodicidad_cambio_canon),
                    e.estado === 'Inicial' ? 'Vigente' : e.estado,
                ]);
            });

            // Bordes
            const border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };

            for (let row = 1; row <= worksheet.rowCount; row++) {
                if ([1, 2, 3].includes(row)) continue;
                for (let col = 1; col <= worksheet.columnCount; col++) {
                    worksheet.getCell(row, col).border = border;
                }
            }

            // Descarga
            const file = await workbook.xlsx.writeBuffer();
            const downloadLink = document.createElement("a");
            downloadLink.download = "Cierre mensual " + this.fechaCierre + ".xlsx";
            downloadLink.href = window.URL.createObjectURL(
                new Blob([file], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                })
            );
            downloadLink.style.display = "none";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            downloadLink.remove();
        },
        nombreTipoTasa(nroTipoTasa) {
            const tipoTasa = this.lstTipoTasa.find(t => t.tipo_tasa === nroTipoTasa);
            return tipoTasa ? tipoTasa.descripcion : null;
        },
        nombreModalidad(m) {
            if (m === 'V') return 'Vencida';
            if (m === 'A') return 'Anticipada';
            if (m === 'F') return 'Fija';
            return '';
        },
        formatNumber(value) {
            return new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(value);
        },
        formatDecimal(value) {
            return new Intl.NumberFormat('es-CO', { minimumFractionDigits: 6, maximumFractionDigits: 6 })?.format(value);
        },
        fechaActual() {
            const fecha = new Date();
            const año = fecha.getFullYear();
            const mes = String(fecha.getMonth() + 1).padStart(2, '0');
            const dia = String(fecha.getDate()).padStart(2, '0');
            return `${año}-${mes}-${dia}`;
        }
    },
    mounted() {
        ContratoCierre.getListadoFechas()
            .then((res) => {
                this.lstFechaCierre = res.data.lstFechaCierre || [];
            })
            .catch((err) => console.error(err));

        TipoTasas.getAllTipoTasas()
            .then((res) => {
                this.lstTipoTasa = res.data.listTipoTasas || [];
            })
            .catch((err) => console.error(err));

        Authentication.leaParametros()
            .then((result) => {
                this.nameCompania = result.data.parametros.compania;
            })
            .catch((err) => console.error(err));
    },
    watch: {
        fechaCierre(val) {
            if (!val) {
                this.listCierreMensual = [];
                this.dataCierreMensual = [];
                this.showToolbarExtras = false;
            }
        }
    }
}
</script>

<style src="../main.css">
</style>

<style scoped>
/* Estilos para el contenedor de filtros - ahora a la izquierda */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

/* Para que el texto de las opciones se vea completo */
.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

/* Estilos para la flecha y título */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-excel-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #016690;
    color: white;
    border: 1px solid #085170;
}

.table-toolbar-excel-btn:hover:not(:disabled) {
    background-color: #085170;
    border-color: #0e3a4d;
}

.table-toolbar-excel-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* Ajustes responsivos */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }

    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }

    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }

    .niif-header .filters-container {
        gap: 4px;
    }

    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
}
</style>